import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Divider,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifySuccess } from "../../utils/notify";
import {
  IconCalendarEvent,
  IconAlertCircle,
  IconInfoCircle,
} from "@tabler/icons-react";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { PackageReasonCategory } from "../../types/packages";
import ReasonFields from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";

interface EditExpiryModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  /** The owned package (client_service_package) id. */
  cspId: string;
  /** Current expiry as an ISO timestamp, or null when the package never expires. */
  currentExpiry: string | null;
  onSuccess: () => void | Promise<void>;
}

/** Format an ISO timestamp to the picker's "YYYY-MM-DD" string, or null. */
function toDateString(iso: string | null): string | null {
  return iso ? moment(iso).format("YYYY-MM-DD") : null;
}

/**
 * Edit (or clear) a client package's expiry. The clearable `DatePickerInput`
 * prefills the current expiry; clearing it sends `null` (never expires).
 * The chosen day is submitted as an end-of-day ISO timestamp so the package
 * stays valid through the whole selected date. Records an `edit_expiry` audit
 * row with a required reason + note.
 */
export default function EditExpiryModal({
  opened,
  onClose,
  salonId,
  cspId,
  currentExpiry,
  onSuccess,
}: EditExpiryModalProps) {
  const { isMobile } = useResponsive();
  const { editClientPackageExpiry } = useApp();

  const currentDateString = toDateString(currentExpiry);

  const [expiry, setExpiry] = useState<string | null>(currentDateString);
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setExpiry(toDateString(currentExpiry));
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened, currentExpiry]);

  const canSubmit = !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    // Keep the package valid through the end of the selected day; null clears it.
    const expiresAt = expiry ? moment(expiry).endOf("day").toISOString() : null;
    try {
      await editClientPackageExpiry(salonId, cspId, {
        expires_at: expiresAt,
        reason_category: category,
        note: note.trim(),
      });
      notifySuccess(
        expiresAt
          ? `Now expires ${moment(expiry).format("MMM D, YYYY")}.`
          : "Package no longer expires.",
        { title: "Expiry updated" },
      );
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in edit-expiry success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error editing expiry:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update the expiry. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Edit Expiry</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <DatePickerInput
          label={
            <Group gap={4} align="center" wrap="nowrap" component="span">
              Expiry date
              <Tooltip
                label="Clear the date to set the package to never expire."
                multiline
                w={240}
                withArrow
                events={{ hover: true, focus: true, touch: true }}
              >
                <IconInfoCircle
                  size={14}
                  style={{
                    cursor: "help",
                    color: "var(--mantine-color-dimmed)",
                  }}
                />
              </Tooltip>
            </Group>
          }
          placeholder="No expiry (never expires)"
          value={expiry}
          onChange={setExpiry}
          clearable
          disabled={isSubmitting}
        />

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
          categoryRequired={false}
          noteRequired={false}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconCalendarEvent size={18} />}
          >
            Save Expiry
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
